/**
 * Base API client for making requests to backend
 */

// Import configuration
import { config } from '../config';

// API base URL from configuration
const API_BASE_URL = config.apiBaseUrl;

// Store authentication state
let isUserAuthenticated = false;

/**
 * Make API request
 *
 * @param {Object} options Request options
 * @param {string} options.url Request URL
 * @param {string} options.method HTTP method
 * @param {Object} options.data Request data
 * @param {Object} options.headers Additional headers
 * @param {boolean} options.bypassAuthCheck Skip authentication check (for auth endpoints)
 * @returns {Promise<Object>} Response data
 */
export const apiRequest = async ({ url, method = 'GET', data = null, headers = {}, bypassAuthCheck = false }) => {
  // Check authentication for non-auth endpoints unless bypassed
  if (!bypassAuthCheck && !url.includes('auth.php') && !isUserAuthenticated) {
    // Try to verify authentication first
    try {
      await refreshAuthState();
    } catch (error) {
      throw new Error('Authentication required');
    }
  }

  try {
    // Prepare request options
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      credentials: 'include', // Include cookies in request
      mode: 'cors'           // Enable CORS mode
    };

    // Add request body for POST/PUT methods
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      options.body = JSON.stringify(data);
    }

    // Make the request
    const response = await fetch(`${API_BASE_URL}${url}`, options);

    // Parse response
    const contentType = response.headers.get('content-type');
    let responseData;

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // Update auth state if this was an auth endpoint
    if (url.includes('auth.php')) {
      if (url.includes('login') && responseData.success) {
        isUserAuthenticated = true;
      } else if (url.includes('logout')) {
        isUserAuthenticated = false;
      } else if (url.includes('check_auth')) {
        isUserAuthenticated = responseData.success && responseData.data.authenticated;
      }
    }

    // Check if response is successful
    if (!response.ok) {
      // If unauthorized, update auth state
      if (response.status === 401) {
        isUserAuthenticated = false;
      }

      const error = new Error(responseData.message || 'API request failed');
      error.response = responseData;
      error.status = response.status;
      throw error;
    }

    return responseData;
  } catch (error) {
    // Handle request errors
    if (!error.status) {
      error.message = 'Network error - please check your connection';
    }

    throw error;
  }
};

/**
 * Refresh authentication state
 *
 * @returns {Promise<boolean>} Authentication state
 */
export const refreshAuthState = async () => {
  try {
    const response = await apiRequest({
      url: '/api/auth.php?method=check_auth',
      method: 'GET',
      bypassAuthCheck: true // Prevent infinite recursion
    });

    isUserAuthenticated = response.success && response.data.authenticated;
    return isUserAuthenticated;
  } catch (error) {
    isUserAuthenticated = false;
    throw error;
  }
};

/**
 * Check if user is authenticated
 *
 * @returns {Promise<boolean>} Authentication status
 */
export const checkAuth = async () => {
  return refreshAuthState();
};

/**
 * Login user
 *
 * @param {string} password Login password
 * @returns {Promise<Object>} Login result
 */
export const login = async (password) => {
  try {
    const response = await apiRequest({
      url: '/api/auth.php?method=login',
      method: 'POST',
      data: { password },
      bypassAuthCheck: true // Login doesn't require auth
    });

    if (response.success) {
      isUserAuthenticated = true;
    }

    return response;
  } catch (error) {
    isUserAuthenticated = false;
    throw error;
  }
};

/**
 * Logout user
 *
 * @returns {Promise<Object>} Logout result
 */
export const logout = async () => {
  try {
    const response = await apiRequest({
      url: '/api/auth.php?method=logout',
      method: 'POST',
      bypassAuthCheck: true // Allow logout even if session expired
    });

    isUserAuthenticated = false;
    return response;
  } catch (error) {
    isUserAuthenticated = false;
    throw error;
  }
};

export default {
  apiRequest,
  checkAuth,
  login,
  logout,
  refreshAuthState
};
